import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { MarketingPreferencesResolverResult } from 'src/app/shared/models/marketing-preferences/marketing-preferences.model';
import { ClientStore } from 'src/app/shared/stores/client/client.store';
import { awaitClientReady } from 'src/app/shared/utils/client/client.util';
import { MarketingPreferencesService } from '../../shared/services/marketingpreferences/marketing-preferences.service';

/**
 * Resolver that fetches marketing preferences for the current client.
 * Checks ClientStore first and only makes API call if data is not available.
 * Returns an object with data and optional error information.
 */
export const marketingPreferencesResolver: ResolveFn<MarketingPreferencesResolverResult> = () => {
  const clientStore = inject(ClientStore);
  const marketingPreferencesService = inject(MarketingPreferencesService);

  return from(awaitClientReady(clientStore)).pipe(
    switchMap((client) => {
      if (!client) {
        return of({ data: null, error: 'Client not ready' });
      }

      const existingPreferences = clientStore.marketingPreferences();

      if (existingPreferences) {
        return of({ data: existingPreferences, error: undefined });
      }

      return marketingPreferencesService.get(client.clientId).pipe(
        map((data) => {
          if (data) clientStore.setMarketingPreferences(data);
          return { data, error: undefined };
        }),
        catchError((error) => {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to load marketing preferences';
          return of({ data: null, error: errorMessage });
        })
      );
    })
  );
};
